import React, { useImperativeHandle } from 'react';
import GenericTab from './GenericTab';
import useSyncObjectRelationships from '../../gql/hooks/useSyncObjectRelationships';
import { Form } from 'antd';
import { TagSelector } from '../../../components/formItems';


/**
 * Tags tab for a create-edit page. 
 * This tab only works once the object exists
 * The page is expected to keep the tab out of reach until
 * then: 
 *   - `tags: [ TAB_STATES.LOCKED ]` in its creationTabStates
 *   - `disabled: isCreating` on the tab item
 *   - `beforeSave` call guarded on the id
 *
 * Tags are not part of any patch input either, so the tab exposes an imperative `onSubmit` for the
 * page's `beforeSave` to await once the id is known
 * 
 * Two things the hosting page owns:
 *
 * - Its formatDataToForm has to put the item's `tagIds` into the form values. saveTags syncs
 *   whatever the form currently holds, so an unseeded field would sync an empty list and strip
 *   every tag off the item on the next save.
 * - antd renders a tab pane lazily, on first activation, so `ref.current` stays undefined until
 *   the user has actually opened this tab, so optional chaining on ref is required
 *
 * @param {Object} ref exposes { onSubmit } which syncs the selected tags for the item
 */
export const TagsTab = ( { ref, form, statuses, parentError, loading, onValidateCb, disabled } ) => {

    const [ syncRelationships ] = useSyncObjectRelationships();

    const formNames = [ 'tagIds' ];

    useImperativeHandle( ref, () => ( {
        onSubmit: ( { objectType, objectId } ) => saveTags( { objectType, objectId } )
    } ) );

    /**
     * Replaces the item's tag attachments with the current selection. Call this after the item has
     * been saved - syncObjectRelationships writes against an id the backend already knows about.
     * @param {string} objectType one of ['course','resource','curriculum',...]
     * @param {string} objectId id of the item being tagged
     */
    async function saveTags( { objectType, objectId } ) {
        await syncRelationships( { variables: { input: {
                objectType,
                objectId,
                relatedResourceType: 'tags',
                relatedResourceIds: form.getFieldValue( 'tagIds' ) ?? []
            } } } );
    }

    // TagSelector is a transfer, so its value arrives as `targetKeys` rather than `value`. Nothing
    // on this tab is required, so it only ever reports UNSAVED - hence makeDirty on change rather
    // than GenericTab's validateTab.
    return <GenericTab loading={ loading } parentError={ parentError } statuses={ statuses } title='Tags' onValidateCb={ onValidateCb } form={ form } formNames={ formNames }>
        { ( { makeDirty } ) => 
            <Form.Item name={ 'tagIds' } valuePropName='targetKeys'>
                <TagSelector disabled={ disabled } onChange={ () => makeDirty() } />
            </Form.Item> 
        }
        </GenericTab>;
};